import React, { useState, useEffect } from 'react';
import {
  VideoCameraIcon,
  CalendarIcon,
  ClockIcon,
  UsersIcon,
  AcademicCapIcon,
  PlayIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';

const StudentLiveSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, upcoming, live, completed

  useEffect(() => {
    fetchLiveSessions();
  }, []);

  const fetchLiveSessions = async () => {
    try {
      setLoading(true);
      const response = await apiService.getMyLiveSessions();
      setSessions(Array.isArray(response) ? response : []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch live sessions:', error);
      setError('Failed to load live sessions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSession = async (session) => {
    try {
      if (session.youtube_watch_url) {
        window.open(session.youtube_watch_url, '_blank');
      } else if (session.meeting_link) {
        window.open(session.meeting_link, '_blank');
      } else {
        setError('No join link available for this session.');
      }
    } catch (error) {
      console.error('Failed to join session:', error);
      setError('Failed to join session. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'private':
        return <EyeIcon className="h-4 w-4 text-gray-500" />;
      case 'unlisted':
        return <EyeSlashIcon className="h-4 w-4 text-yellow-500" />;
      case 'public':
        return <EyeIcon className="h-4 w-4 text-green-500" />;
      default:
        return <EyeIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getFilteredSessions = () => {
    switch (filter) {
      case 'upcoming':
        return sessions.filter(session => 
          session.status === 'scheduled' || session.status === 'draft'
        );
      case 'live':
        return sessions.filter(session => session.status === 'live');
      case 'completed':
        return sessions.filter(session => session.status === 'completed');
      default:
        return sessions;
    }
  };

  const filteredSessions = getFilteredSessions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-10 pt-6 pb-10">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Live Sessions</h1>
            <p className="text-gray-600 mt-1">
              Join your scheduled live classes and interactive sessions
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <VideoCameraIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex space-x-1">
          {[
            { key: 'all', label: 'All Sessions', count: sessions.length },
            { key: 'upcoming', label: 'Upcoming', count: sessions.filter(s => s.status === 'scheduled' || s.status === 'draft').length },
            { key: 'live', label: 'Live Now', count: sessions.filter(s => s.status === 'live').length },
            { key: 'completed', label: 'Completed', count: sessions.filter(s => s.status === 'completed').length },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === tab.key
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Sessions Grid */}
      {filteredSessions.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSessions.map((session) => (
            <div key={session.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                {/* Session Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <VideoCameraIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{session.title}</h3>
                      <p className="text-sm text-gray-500">{session.course_name || 'Course'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {session.status === 'live' && (
                      <div className="flex items-center text-green-600">
                        <div className="w-2 h-2 bg-green-600 rounded-full mr-1 animate-pulse"></div>
                        <span className="text-xs font-medium">LIVE</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Session Description */}
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {session.description}
                </p>

                {/* Session Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {new Date(session.scheduled_time).toLocaleDateString()}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    {new Date(session.scheduled_time).toLocaleTimeString()} ({session.duration_minutes} min)
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <UsersIcon className="h-4 w-4 mr-2" />
                    {session.total_students_eligible || 0} eligible students
                  </div>
                  {session.batches && session.batches.length > 0 && (
                    <div className="flex items-center text-sm text-gray-500">
                      <AcademicCapIcon className="h-4 w-4 mr-2" />
                      {session.batches.length} batch(es)
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-500">
                    {getPrivacyIcon(session.privacy_status)}
                    <span className="ml-2 capitalize">{session.privacy_status}</span>
                  </div>
                </div>

                {/* Session Status and Actions */}
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                    {session.status}
                  </span>
                  
                  {/* Join Button */}
                  {session.status === 'live' && (
                    <button
                      onClick={() => handleJoinSession(session)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <PlayIcon className="h-4 w-4 mr-2" />
                      Join Now
                    </button>
                  )}
                  
                  {/* Upcoming Session Info */}
                  {session.status === 'scheduled' && (
                    <div className="text-sm text-gray-500">
                      Starts in {Math.max(0, Math.floor((new Date(session.scheduled_time) - new Date()) / (1000 * 60)))} minutes
                    </div>
                  )}
                </div>

                {/* Additional Notes */}
                {session.notes && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>Notes:</strong> {session.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
          <VideoCameraIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-semibold text-gray-900">
            {filter === 'all' ? 'No live sessions available' : `No ${filter} sessions`}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {filter === 'all' 
              ? 'You don\'t have any live sessions scheduled yet.'
              : `You don't have any ${filter} sessions at the moment.`
            }
          </p>
          {filter !== 'all' && (
            <button
              onClick={() => setFilter('all')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
            >
              View All Sessions
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentLiveSessions;
